import * as duckdb from "@duckdb/duckdb-wasm";
import type { AsyncDuckDB, ILogger, LogLevel } from "@duckdb/duckdb-wasm";
import duckdb_mvp_wasm from "@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url";
import mvp_worker from "@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url";
import duckdb_eh_wasm from "@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url";
import eh_worker from "@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url";
import { useState, useEffect } from "react";
import { tokenizeSQL, SQLToken } from "./tokenize";  // ← import tokenizer

/** Filter DEBUG/INFO; surface only WARN/ERROR. */
export class FilteredLogger implements ILogger {
  log(level: LogLevel, message: string, topic?: number, origin?: number): void {
    if (level >= 2) {
      const tag = level === 2 ? "DuckDB WARN" : "DuckDB ERROR";
      const fn = level === 2 ? console.warn : console.error;
      fn(`[${tag}]`, message, { topic, origin });
    }
  }
}

/** Initialise DuckDB-WASM and return { db, conn, loading, error }. */
export function useDuckDBConnection() {
  const [db, setDB] = useState<AsyncDuckDB | null>(null);
  const [conn, setConn] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const bundles: duckdb.DuckDBBundles = {
          mvp: { mainModule: duckdb_mvp_wasm, mainWorker: mvp_worker },
          eh: { mainModule: duckdb_eh_wasm, mainWorker: eh_worker },
        };
        const bundle = await duckdb.selectBundle(bundles);
        const worker = new Worker(bundle.mainWorker!, { type: "module" });
        const logger = new FilteredLogger();
        const dbInst = new duckdb.AsyncDuckDB(logger, worker);

        await dbInst.instantiate(bundle.mainModule, bundle.pthreadWorker);
        await dbInst.open({});
        const connection = await dbInst.connect();

        /* Enable HTTPFS so we can stream remote files. */
        try {
          await connection.query("INSTALL httpfs; LOAD httpfs;");
        } catch {
          /* already installed? ignore */
        }

        // ── **Expose our new tokenizer** ───────────────────
        // Attach an async `.tokenize(sql)` method that yields SQLToken[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (connection as any).tokenize = (sql: string): Promise<SQLToken[]> =>
          tokenizeSQL(sql, connection);

        setDB(dbInst);
        setConn(connection);
        setLoading(false);
      } catch (err) {
        console.error("Error instantiating DuckDB:", err);
        setError(err as Error);
        setLoading(false);
      }
    })();
  }, []);

  return { db, conn, loading, error };
}
